import {
    generatePath,
    match as IRouterMatch,
    matchPath,
    RouteProps,
    useHistory
} from 'react-router-dom';
import { isObject } from './utils';

const isRouteInstance = target => (isObject(target) && target.path);

/* eslint-disable arrow-body-style */
const route = (value: object) => {
    return function(target: any, key: string) {
        target[key] = {
            ...value,
            'construct': function(
                url_params: {[paramName: string]: string | number | boolean | undefined;}
            ) {
                return generatePath(this.path, url_params);
            },
            'toString': function() {
                return this.path;
            }
        };
    };
};
/* eslint-enable arrow-body-style */

interface IRouteObject extends Partial<RouteProps> {
    // Имеет возможность подставить в роут с переменными переменные url_params
    construct?: (
        url_params: {
            [paramName: string]: string | number | boolean | undefined;
        }
    ) => string;
}

export type TRouteLabels = Map<IRouteObject, string>;

export interface IObjectRouteParams extends IMapRouteParams {
    layerCode: string;
    objectId: string;
}

export interface IMapRouteParams {
    center?: string;
    zoom?: string;
    layers?: string;
    additional?: string;
    container: string;
}

class Routes {
    history: ReturnType<typeof useHistory>;
    navigation: {route: IRouteObject, params: IObjectRouteParams}[] = [];
    currRouteState: { [key: string]: any } = {};

    @route({ 'path': '/', exact: true }) root: IRouteObject;
    @route({ 'path': '/map/:container/:center?/:zoom?/:layers?', exact: true }) map: IRouteObject;
    @route({ 'path': '/filters', exact: true }) filters: IRouteObject;
    @route({ 'path': '/submit-application', exact: true }) submitForm: IRouteObject;
    @route({ 'path': '/object/:layerCode/:objectId/:center?/:zoom?/:layers?', exact: true }) object: IRouteObject;
    @route({ 'path': '/list/:layerCode/:center?/:zoom?/:layers?', exact: true }) list: IRouteObject;
    @route({ 'path': '/list/favourites/:center?/:zoom?/:layers?', exact: true }) favourites: IRouteObject;
    @route({ 'path': '/:layerCode/:objectId', exact: true }) objectShort: IRouteObject;
    @route({ 'path': '/compare/:center?/:zoom?/:layers?', exact: true }) compareObjects: IRouteObject;

    * [Symbol.iterator]() {
        for (let [ route_object, label ] of this.labels.entries()) {
            yield route_object;
        }
    }

    public labels: TRouteLabels = new Map([
        [ this.root, 'Главная' ],
        [ this.map, 'Карта' ],
        [ this.filters, 'Окно подбора площадки' ],
        [ this.submitForm, 'Получить консультацию' ],
        [ this.object, 'Окно объекта' ],
        [ this.list, 'Окно списка объектов' ],
        [ this.favourites, 'Избранное' ],
        [ this.compareObjects, 'Окно сравнения объектов' ],
        [ this.objectShort, 'Окно объекта' ]
    ]);

    public buildFullPath(
        path: string,
        params?: IRouterMatch['params']
    ): TRouteLabels {
        let path_map: [IRouteObject, string][] = [];

        let path_parts = path.split('/');

        for (let i = path_parts.length; i > 1; i--) {
            let current_path_parts = path_parts.slice(0, i);
            let current_path = current_path_parts.join('/');

            this.labels.forEach((default_label, route_object) => {
                if (matchPath(current_path, route_object)) {
                    let label = default_label;

                    if (params && Object.keys(params).length) {
                        label = generatePath(label, params);
                    }

                    path_map.unshift([ route_object, label ]);
                }
            });
        }

        // Добавляем главную страницу
        path_map.unshift(
            [ this.root, this.labels.get(this.root) as string ]
        );

        return new Map(path_map);
    }

    applyHistory(history) {
        if (!this.history) {
            this.history = history;
        }
    }

    getCurrentRoute() {
        let matchResult;
        for (let _route of this) {
            if (isRouteInstance(_route)) {
                const res = matchPath(this.history.location.pathname, _route);
                if (res !== null && !matchResult) {
                    matchResult = { ...res, route: _route };
                }
            }
        }
        return matchResult;
    }

    setMapParams(params) {
        let _params = { ...params };
        if (_params.layers !== undefined && _params.layers === '') {
            _params.layers = undefined;
        }

        const currentRoute = this.getCurrentRoute();
        if (currentRoute) {
            const path = currentRoute.route.construct({
                ...currentRoute.params,
                ..._params
            });
            this.history.replace({
                pathname: path
                // state: this.history.location.state
            });
        }
    }

    redirect(to: IRouteObject, params = {}, push = true, state = {}) {
        const currentRoute = this.getCurrentRoute();
        const { center, layers, zoom, ...currentRouteParams } = currentRoute.params;
        const path = to.construct({
            ...currentRoute.params,
            ...params
        });
        if (push) {
            if (path !== this.history.location.pathname) {
                this.navigation.push({
                    route: currentRoute.route,
                    params: currentRouteParams
                });
            }

            this.currRouteState = state;
            this.history.push({
                pathname: path,
                state
            });
        } else {
            this.currRouteState = state;
            this.history.replace({
                pathname: path,
                state
            });
        }
    }

    goBack(defaultTo: IRouteObject, params = {}, state = {}) {
        const prev = this.navigation.pop();
        if (prev) {
            this.redirect(prev.route, prev.params, false, state);
        } else {
            this.redirect(defaultTo, params, false);
        }
    }
}

export const routes = new Routes();
